/**
 * API client for the partner-portal surface (§14) — REAL endpoints.
 *
 * The routes under `/api/v1/mock/portal/*` were gutted and rebuilt on
 * live data (PMS partner row + prod payments emulation + DW launches)
 * at the full-app-completion cutover. The *paths* were deliberately
 * kept: the router registry is single-author and the PWA runtime cache
 * names a `fz-api-mock` bucket for this prefix (see `vite.config.ts`);
 * an `/api/v1/portal` alias is a one-line follow-up if the rename is
 * ever wanted.
 *
 * Auth: every route is partner-scoped (`require_partner_scope`) —
 * internal users get **403**; managers reach it via the hijack flow.
 * IBAN/email mutations persist to the PMS partner row (OTP-gated) and
 * payment statuses reflect the internal Paid flag.
 *
 * Function-based shape is intentional (no `BaseApiService` extension
 * since there's no CRUD surface to inherit). Errors propagate — views
 * handle their own loading / error states.
 */
import type { AxiosResponse } from 'axios'

import apiClient from '@/api/client'
import type {
  AccountChangeConfirmResponse,
  EmailChangeRequestPayload,
  IbanChangeRequestPayload,
  OtpConfirmPayload,
  OtpRequestResponse,
  PortalCouponStats,
  PortalPaymentsResponse,
  PortalSelfResponse,
} from '@/types/portal'

/** GET `/api/v1/mock/portal/self` — identity + coupons + 12-month series (real, partner-scoped). */
export function fetchSelf(): Promise<AxiosResponse<PortalSelfResponse>> {
  return apiClient.get<PortalSelfResponse>('/mock/portal/self')
}

/** GET `/api/v1/mock/portal/payments` — last 12 calendar months of payouts; stato per month
 *  = `Pagato` / `In approvazione` / `—` from the real payment rows. */
export function fetchPayments(): Promise<AxiosResponse<PortalPaymentsResponse>> {
  return apiClient.get<PortalPaymentsResponse>('/mock/portal/payments')
}

/** GET `/api/v1/mock/portal/coupons/{code}/stats` — lifetime totals (DW launches) +
 *  30-day dual series. `clicks` is always 0 (no tracking source) — the FE hides the tile. */
export function fetchCouponStats(code: string): Promise<AxiosResponse<PortalCouponStats>> {
  return apiClient.get<PortalCouponStats>(`/mock/portal/coupons/${encodeURIComponent(code)}/stats`)
}

// ──────────────────────────────────────────────────────────────────
// Account-mutation endpoints (IBAN / email change with OTP confirm).
// ──────────────────────────────────────────────────────────────────

/** POST `/api/v1/mock/portal/account/iban/request` — kick off an OTP-gated IBAN change. */
export function requestIbanChange(
  payload: IbanChangeRequestPayload,
): Promise<AxiosResponse<OtpRequestResponse>> {
  return apiClient.post<OtpRequestResponse>('/mock/portal/account/iban/request', payload)
}

/** POST `/api/v1/mock/portal/account/iban/confirm` — confirm OTP; persists to the PMS partner row. */
export function confirmIbanChange(
  payload: OtpConfirmPayload,
): Promise<AxiosResponse<AccountChangeConfirmResponse>> {
  return apiClient.post<AccountChangeConfirmResponse>('/mock/portal/account/iban/confirm', payload)
}

/** POST `/api/v1/mock/portal/account/email/request` — kick off an OTP-gated email change. */
export function requestEmailChange(
  payload: EmailChangeRequestPayload,
): Promise<AxiosResponse<OtpRequestResponse>> {
  return apiClient.post<OtpRequestResponse>('/mock/portal/account/email/request', payload)
}

/** POST `/api/v1/mock/portal/account/email/confirm` — confirm OTP; persists to the PMS partner row. */
export function confirmEmailChange(
  payload: OtpConfirmPayload,
): Promise<AxiosResponse<AccountChangeConfirmResponse>> {
  return apiClient.post<AccountChangeConfirmResponse>('/mock/portal/account/email/confirm', payload)
}
